// AppRailV2 — slim 48px vertical view-switcher (matches the V1 design's
// compact left nav). URL-driven: each entry navigates to a route, and the
// active entry is computed from the current pathname. Renders nothing
// app-specific; consumers pass the views list and an optional logo node.

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';

export interface AppRailV2View {
    id: string;
    label: string;
    icon: React.ReactNode;
    /** Route the view navigates to (e.g. '/dashboards'). */
    to: string;
    /**
     * Optional matcher. By default the view is active when pathname starts
     * with `to`. Use this when two views share a prefix.
     */
    isActive?: (pathname: string) => boolean;
}

export interface AppRailV2Props {
    views: AppRailV2View[];
    /** Logo / brand mark rendered at the top of the rail. */
    logo?: React.ReactNode;
    /** Optional content rendered at the bottom (avatar, settings). */
    footer?: React.ReactNode;
}

const RAIL_WIDTH = 48;

export const AppRailV2: React.FC<AppRailV2Props> = ({ views, logo, footer }) => {
    const { pathname } = useLocation();

    return (
        <Box
            component="nav"
            sx={(theme) => ({
                width: RAIL_WIDTH,
                flexShrink: 0,
                backgroundColor: theme.palette.background.paper,
                borderRight: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                py: 1.5,
                gap: 0.5,
            })}
            aria-label="Primary"
        >
            {logo && <Box sx={{ mb: 1 }}>{logo}</Box>}

            {views.map((view) => {
                const active = view.isActive
                    ? view.isActive(pathname)
                    : pathname === view.to || pathname.startsWith(view.to + '/');
                return (
                    <Tooltip key={view.id} title={view.label} placement="right">
                        <Box
                            component={Link}
                            to={view.to}
                            sx={(theme) => ({
                                width: 32,
                                height: 32,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderRadius: `${theme.shape.radiusSm}px`,
                                // Active icon picks up the brand amber so
                                // it reads as "selected" at a glance — the
                                // previous text-primary + raised-bg combo
                                // was too subtle, especially next to the
                                // breadcrumb in the AppBar that names the
                                // same view.
                                color: active ? theme.palette.primary.main : theme.palette.text.disabled,
                                backgroundColor: active ? theme.palette.surface.hover : 'transparent',
                                textDecoration: 'none',
                                transition: 'background-color .12s, color .12s',
                                '&:hover': {
                                    backgroundColor: active ? theme.palette.surface.hover : theme.palette.surface.raised,
                                    color: active ? theme.palette.primary.main : theme.palette.text.primary,
                                },
                            })}
                            aria-label={view.label}
                            aria-current={active ? 'page' : undefined}
                        >
                            {view.icon}
                        </Box>
                    </Tooltip>
                );
            })}

            <Box sx={{ flex: 1 }} />
            {footer}
        </Box>
    );
};

AppRailV2.displayName = 'AppRailV2';
